import { useQuery } from '@tanstack/react-query';
import { fetchPartnerAvailability } from '../api/partnerAvailability';
import type { PartnerAvailabilityResponse } from '../api/partnerAvailability';

// ---------------------------------------------------------------------------
// Query key
// ---------------------------------------------------------------------------

export const partnerAvailabilityKeys = {
  all: ['partnerAvailability'] as const,
  byParams: (restaurantId: number, partySize: number, serviceType: string, date: string) =>
    [...partnerAvailabilityKeys.all, restaurantId, partySize, serviceType, date] as const,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UsePartnerAvailabilityOptions {
  restaurantId: number | undefined;
  partySize: number;
  serviceType: string | null;
  date: string | null;
  /** Only fetch when there are no available slots */
  hasNoSlots: boolean;
  /** Feature flag: restaurant.offerCrossAvailabilityFromPartners */
  featureEnabled: boolean;
  /** Restaurant has partner restaurants configured */
  hasPartners: boolean;
  /** Disable for special flows (waiting list, special events, etc.) */
  isSpecialFlow?: boolean;
}

interface UsePartnerAvailabilityResult {
  data: PartnerAvailabilityResponse | undefined;
  isLoading: boolean;
}

/**
 * Fetches partner availability when the current restaurant has no time slots.
 * Only enabled when:
 * - No slots available
 * - Feature flag is on
 * - Partners exist
 * - Not in a special flow
 */
export function usePartnerAvailability({
  restaurantId,
  partySize,
  serviceType,
  date,
  hasNoSlots,
  featureEnabled,
  hasPartners,
  isSpecialFlow = false,
}: UsePartnerAvailabilityOptions): UsePartnerAvailabilityResult {
  const enabled =
    hasNoSlots &&
    featureEnabled &&
    hasPartners &&
    !isSpecialFlow &&
    !!restaurantId &&
    !!serviceType &&
    !!date &&
    partySize > 0;

  const { data, isLoading } = useQuery({
    queryKey: partnerAvailabilityKeys.byParams(
      restaurantId!,
      partySize,
      serviceType!,
      date!,
    ),
    queryFn: () => fetchPartnerAvailability(restaurantId!, partySize, serviceType!, date!),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    data: enabled ? data : undefined,
    isLoading: enabled && isLoading,
  };
}
